// Les réactions, côté écran : une pastille, une palette, un résumé.
//
// Trois surfaces s'en servent — les publications, les messages, les statuts —
// et elles doivent se ressembler. Ce fichier est ce qu'elles partagent ; ce qui
// EXISTE comme réaction est ailleurs, dans `js/reactions.js`.

import { useEffect, useRef, useState } from "react";
import { REACTIONS, reaction, resumer, resumerCompteurs } from "../reactions.js";

export { resumer, resumerCompteurs };

/**
 * Une réaction, dessinée. La brique de tout le reste.
 *
 * L'émoji est posé à la taille demandée, sans fond : il porte sa propre
 * couleur, et un disque derrière lui ne ferait que l'assombrir.
 *
 * `role="img"` et `aria-label` : sans eux, un lecteur d'écran lirait le nom
 * Unicode du caractère — « visage qui pleure » plutôt que « Triste ».
 */
export function Pastille({ cle, taille = 20, titre }) {
  const r = reaction(cle);
  return (
    <span
      className={`reaction-pastille reaction-pastille--${r.cle}`}
      style={{ width: taille, height: taille, fontSize: Math.round(taille * 0.92) }}
      title={titre === undefined ? r.label : titre}
      role="img"
      aria-label={r.label}
    >
      {r.emoji}
    </span>
  );
}

/**
 * L'appui long — celui qui ouvre la palette.
 *
 * ⚠️ Il faut les DEUX familles d'événements, et c'est le genre de détail qui
 * fait qu'une fonction « marche sur mon ordinateur » et nulle part ailleurs :
 * `pointerdown` couvre la souris et le tactile, mais sur un téléphone le
 * navigateur ouvre SON menu à lui (copier, partager…) au bout du même délai.
 * D'où `onContextMenu` neutralisé, et le mouvement qui annule : un doigt qui
 * part en défilement ne demandait pas de palette.
 */
export function useAppuiLong(quandLong, { delai = 420 } = {}) {
  const minuteur = useRef(null);
  const depart = useRef(null);
  const declenche = useRef(false);

  const annuler = () => {
    clearTimeout(minuteur.current);
    minuteur.current = null;
  };

  useEffect(() => annuler, []);

  return {
    // `aLongue` dit à l'appelant si l'appui court doit encore compter : après
    // une palette ouverte, le relâchement ne doit pas AUSSI poser un Amen.
    aLongue: () => declenche.current,
    handlers: {
      onPointerDown: (e) => {
        declenche.current = false;
        depart.current = { x: e.clientX, y: e.clientY };
        annuler();
        minuteur.current = setTimeout(() => {
          declenche.current = true;
          quandLong();
        }, delai);
      },
      onPointerMove: (e) => {
        if (!depart.current || !minuteur.current) return;
        const d = Math.hypot(e.clientX - depart.current.x, e.clientY - depart.current.y);
        if (d > 12) annuler(); // c'est un défilement, pas un appui
      },
      onPointerUp: annuler,
      onPointerLeave: annuler,
      onPointerCancel: annuler,
      onContextMenu: (e) => e.preventDefault(),
    },
  };
}

/**
 * La palette : les sept, en rangée, au-dessus de ce qu'on commente.
 *
 * Elle se referme au premier clic hors d'elle et à la touche Échap — sans quoi
 * elle resterait ouverte derrière l'écran suivant. Le clic de fermeture est
 * écouté en phase de CAPTURE : posé en phase normale, il partirait après le
 * clic du bouton qui vient de la rouvrir, et la palette clignoterait.
 */
export function Palette({ mienne, onChoisir, onFermer, className = "" }) {
  const boite = useRef(null);

  useEffect(() => {
    const dehors = (e) => {
      if (!boite.current?.contains(e.target)) onFermer();
    };
    const touche = (e) => e.key === "Escape" && onFermer();
    document.addEventListener("pointerdown", dehors, true);
    document.addEventListener("keydown", touche);
    return () => {
      document.removeEventListener("pointerdown", dehors, true);
      document.removeEventListener("keydown", touche);
    };
  }, [onFermer]);

  return (
    <div className={`reaction-palette ${className}`} ref={boite} role="menu">
      {REACTIONS.map((r) => (
        <button
          key={r.cle}
          type="button"
          role="menuitemradio"
          aria-checked={mienne === r.cle}
          className={`reaction-choix${mienne === r.cle ? " est-actif" : ""}`}
          onClick={() => onChoisir(mienne === r.cle ? null : r.cle)}
        >
          <Pastille cle={r.cle} taille={34} titre="" />
          <span className="reaction-choix__label">{r.label}</span>
        </button>
      ))}
    </div>
  );
}

/**
 * Le résumé : les pastilles empilées, puis le nombre.
 *
 * Ne rend RIEN quand personne n'a réagi. Un « 0 » sous chaque message serait du
 * bruit permanent pour l'information la plus rare.
 */
export function Resume({ total, tetes, onClick, className = "", avecNombre = true }) {
  if (!total) return null;
  const contenu = (
    <>
      <span className="reaction-resume__pile">
        {tetes.map((cle) => (
          <Pastille key={cle} cle={cle} taille={17} />
        ))}
      </span>
      {/* Sous une publication, le nombre est déjà dans la phrase qui suit
          (« 8 personnes prient ») : le répéter ici donnerait « 8 · 8 personnes
          prient ». Les pastilles disent QUOI, la phrase dit COMBIEN. */}
      {avecNombre && <span className="reaction-resume__nombre">{total}</span>}
    </>
  );
  if (!onClick) return <span className={`reaction-resume ${className}`}>{contenu}</span>;
  return (
    <button type="button" className={`reaction-resume ${className}`} onClick={onClick}>
      {contenu}
    </button>
  );
}

/**
 * Tout l'état d'un bouton de réaction : la palette ouverte ou non, et l'appui
 * long qui l'ouvre. Les trois écrans en avaient besoin à l'identique.
 */
export function usePalette() {
  const [ouverte, setOuverte] = useState(false);
  const appui = useAppuiLong(() => setOuverte(true));
  return {
    ouverte,
    ouvrir: () => setOuverte(true),
    fermer: () => setOuverte(false),
    appui,
    /** Vrai si l'appui court doit être ignoré — la palette vient de s'ouvrir. */
    absorbe: () => appui.aLongue(),
  };
}
